import { DestroyRef, inject, Injectable, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest, defer, Observable, of, Subject } from 'rxjs';
import { debounceTime, filter, finalize, map, scan, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoadingScreenComponent } from './loading-screen.component';

@Injectable({
  providedIn: 'root'
})
export class LoadingService implements OnDestroy {
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  private loadingScreen: MatDialogRef<LoadingScreenComponent>;
  private readonly loadingCounter$ = new BehaviorSubject(0);
  private readonly shouldLoad$ = new BehaviorSubject(false);
  private readonly unsubscribe$ = new Subject<void>();
  private readonly abortDismiss$ = new Subject<void>();
  private readonly isSuppressed$ = new BehaviorSubject(false);
  private readonly isScreenOpen$ = new BehaviorSubject(false);
  private readonly screenOpened$ = new Subject<MatDialogRef<LoadingScreenComponent>>();

  public isLoading$ = new BehaviorSubject(false);

  constructor() {
    this.abortDismiss$.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.subscribeToDismiss();
    });
    this.loadingCounter$
      .pipe(
        scan((previous, increment) => {
          const newCount = previous + increment < 0 ? 0 : previous + increment;
          this.shouldLoad$.next(newCount > 0);
          return newCount;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
    this.subscribeToPresent();
    this.subscribeToScreenClosed();
  }

  public present(): void {
    this.loadingCounter$.next(1);
    this.abortDismiss$.next();
  }

  public dismiss(): void {
    this.loadingCounter$.next(-1);
  }

  private subscribeToPresent(): void {
    combineLatest([this.shouldLoad$, this.isSuppressed$])
      .pipe(debounceTime(0), takeUntil(this.unsubscribe$))
      .subscribe(([shouldLoad, isSuppressed]) => {
        if (shouldLoad && !this.loadingScreen && !isSuppressed) {
          this.loadingScreen = this.dialog.open(LoadingScreenComponent, {
            minWidth: '100vw',
            minHeight: '100vh',
            disableClose: true,
            hasBackdrop: true
          });
          this.isScreenOpen$.next(true);
          this.screenOpened$.next(this.loadingScreen);
        }
      });
  }

  private subscribeToScreenClosed(): void {
    this.screenOpened$
      .pipe(
        switchMap(screen =>
          screen.afterClosed().pipe(
            take(1),
            map(() => screen)
          )
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(screen => {
        // Also closed from outside, e.g. by MatDialog.closeAll(), which must not leave whenIdle() waiting
        if (this.loadingScreen === screen) this.loadingScreen = null;
        this.isScreenOpen$.next(false);
      });
  }

  private subscribeToDismiss(): void {
    combineLatest([this.shouldLoad$, this.isSuppressed$])
      .pipe(debounceTime(100), takeUntil(this.abortDismiss$), takeUntil(this.unsubscribe$))
      .subscribe(([shouldLoad, isSuppressed]) => {
        if ((!shouldLoad || isSuppressed) && this.loadingScreen) {
          this.loadingScreen.close();
          this.loadingScreen = null;
        }
      });
  }

  /** Emits once nothing is loading and the loading screen is fully closed, e.g. before opening a dialog that must not overlap it. */
  public whenIdle(): Observable<void> {
    return combineLatest([this.shouldLoad$, this.isScreenOpen$]).pipe(
      filter(([shouldLoad, isScreenOpen]) => !shouldLoad && !isScreenOpen),
      take(1),
      map(() => undefined)
    );
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.abortDismiss$.complete();
  }

  public withLoadingScreen = <T>(observable$: Observable<T>): Observable<T> =>
    of(null).pipe(
      tap(() => this.present()),
      switchMap(() => observable$),
      finalize(() => this.dismiss())
    );

  /** Prevents the loading screen from displaying while the observable is active */
  public suppressLoadingScreen =
    (suppress = true) =>
    <T>(observable$: Observable<T>): Observable<T> => {
      return suppress
        ? defer(() => {
            this.isSuppressed$.next(true);
            return observable$.pipe(finalize(() => this.isSuppressed$.next(false)));
          })
        : observable$;
    };
}
